/**
 * Rendering compare mode: an inventory diff between two repos, and one tensor's
 * A-against-B comparison (plan §4 v2).
 *
 * Pure string-returning functions, like `tensor-view.ts` and `anatomy-map.ts` —
 * no DOM mutation and no listeners here; `main.ts` owns the wiring. The charts
 * are hand-rolled SVG for the same reason they are there: the marks are simple
 * and the constraints this project works under (thin marks, a recessive grid,
 * direct labels, a table alternative) are easier to satisfy directly than to
 * talk a charting library out of its defaults.
 *
 * **The layout is dictated by `compare.ts`, not by taste.** That module returns
 * its statistics in pairs and says why: a size statistic is meaningless without
 * the shape statistic beside it. `relF` alone ranks a surgical change and a
 * diffuse one identically; `cosine` alone cannot tell a rotation from a rescale.
 * So the pairs share a card here — never two cards, never a card and a table —
 * because a reader who can see one without the other has been handed the
 * misleading half of the answer. If a future edit wants to "tidy" these into a
 * flat stat grid, that edit is the regression.
 *
 * **Colour.** Three identities in fixed slot order, assigned by identity and
 * never by rank: A is `--series-1`, B is `--series-2`, the delta is
 * `--series-3`. The delta gets its own slot rather than borrowing A's because
 * it appears in its own chart and must never be mistaken for either side. The
 * three-slot set was run through the data-viz palette validator against both
 * surfaces — all-pairs, since the two overlaid distributions genuinely sit on
 * top of each other — and passes every gate in both modes (worst all-pairs CVD
 * ΔE 9.2 light / 9.4 dark; worst normal-vision ΔE 24.0 light / 20.9 dark). The
 * one WARN is light-mode contrast: `--series-3` sits at 2.82:1 on white, which
 * obliges visible labels or a table view. Both ship below, which is the same
 * obligation `tensor-view.ts` already discharges the same way.
 *
 * **Overlap treatment.** Two distributions on one range are drawn as a ~10%
 * area wash under a 2px stepped outline each — the documented mark specs, not
 * an invented transparency. The wash shows where the mass is, the outline
 * survives being overlapped, and neither series is drawn on top in a way that
 * hides the other.
 */

import type { TensorComparison } from "../analysis/compare.js";
import type { Histogram } from "../analysis/histogram.js";
import {
  type InventoryDiff,
  type TensorDiffEntry,
  type TensorDiffStatus,
  UNRELATED_OVERLAP,
} from "../analysis/inventory-diff.js";
import type { InventoryTensor } from "../safetensors/inventory.js";
import type { CompareResult, SharedHistograms } from "../worker/protocol.js";
import { cardSummaryTitle, cardTitle } from "./card.js";
import { type ExplainKey, explainIcon } from "./explain.js";
import { formatBytes, formatParams, formatShape } from "./format.js";

const CHART_WIDTH = 880;
const OVERLAY_HEIGHT = 220;
const DELTA_HEIGHT = 170;
const CHART_PAD = 48;

/** A is the baseline, B the derived side, the delta a third identity. */
const SERIES_A = "var(--series-1)";
const SERIES_B = "var(--series-2)";
const SERIES_DELTA = "var(--series-3)";

/*
 * The overlap floor is imported, not restated.
 *
 * `inventory-diff.ts` applies the same threshold when it composes
 * `diff.verdict`, and this view *leads* with that verdict — so a lower number
 * here would print "These two do not look related" as the headline and then
 * render six hundred rows of diff underneath it, which reads as the tool
 * contradicting itself. Two constants that must agree are a bug waiting for
 * someone to tune one of them.
 */

/**
 * Rows the entry table will draw before it truncates.
 *
 * Identical tensors are already excluded (they are counted, not listed), so a
 * real finetune diff lands far under this. A pathological one gets a stated
 * truncation rather than a page that takes a second to lay out.
 */
const MAX_ENTRY_ROWS = 200;

/** Participation at or above this reads as uniform drift across the tensor. */
const PARTICIPATION_UNIFORM = 0.85;
/** Participation below this reads as a change concentrated in a few weights. */
const PARTICIPATION_CONCENTRATED = 0.2;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Four significant figures is the resolution these statistics actually carry. */
function sig(value: number | undefined, digits = 4): string {
  if (value === undefined || !Number.isFinite(value)) return "—";
  return Number(value).toPrecision(digits);
}

function percent(fraction: number, digits = 2): string {
  if (!Number.isFinite(fraction)) return "—";
  return `${(fraction * 100).toFixed(digits)}%`;
}

/**
 * A statistic that may be absent, and the sentence that says why.
 *
 * `cosine`, `relF` and `deltaParticipation` are `number | undefined` by design
 * — a zero-norm side has no direction, an identically-zero delta has no
 * concentration — and the contract is explicit that they are `undefined` and
 * never NaN so the UI can render an em dash *on purpose*. A silent blank would
 * read as a rendering bug and "NaN" would read as a broken statistic; both are
 * wrong about a number that is legitimately not defined here.
 */
interface MaybeStat {
  readonly label: string;
  readonly text: string;
  readonly reason: string | undefined;
}

function maybeStat(
  label: string,
  value: number | undefined,
  reason: string,
  digits = 4,
): MaybeStat {
  if (value === undefined || !Number.isFinite(value)) {
    return { label, text: "—", reason };
  }
  return { label, text: sig(value, digits), reason: undefined };
}

/** The notes that explain every dash in a card, or nothing when there are none. */
function reasonNotes(stats: readonly MaybeStat[]): string {
  const notes = stats.filter((s) => s.reason !== undefined);
  if (notes.length === 0) return "";
  return notes
    .map(
      (s) =>
        `<p class="chart-note"><strong>${escapeHtml(s.label)} is not defined here.</strong>
         ${escapeHtml(s.reason ?? "")}</p>`,
    )
    .join("");
}

/** Label, value, and the glossary entry the label's icon opens. */
type StatCell = readonly [string, string, ExplainKey];

function statCell(label: string, value: string, key: ExplainKey): string {
  return `<div><dt>${escapeHtml(label)}${explainIcon(key)}</dt><dd class="mono">${escapeHtml(value)}</dd></div>`;
}

function statGrid(cells: ReadonlyArray<StatCell>): string {
  return `<dl class="stat-grid">${cells
    .map(([label, value, key]) => statCell(label, value, key))
    .join("")}</dl>`;
}

// --- Inventory diff ---------------------------------------------------------

const STATUS_LABEL: Record<TensorDiffStatus, string> = {
  identical: "identical",
  "dtype-changed": "dtype changed",
  quantized: "quantized",
  dequantized: "dequantized",
  "shape-changed": "shape changed",
  "only-a": "only in A",
  "only-b": "only in B",
};

/**
 * Tensors that exist on both sides, counted from the entries rather than from
 * the summary.
 *
 * The summary's counters do partition `entries`, so summing the non-one-sided
 * ones would also work. This does not, deliberately: "matched" means "not
 * one-sided", and saying that directly cannot be broken by a future status
 * being added to `TensorDiffStatus` and forgotten here. An earlier revision of
 * the contract was missing the `dequantized` counter, and summing the rest
 * scored a repo pair where B is a dequantization of A at nothing in common —
 * sending the most interesting lineage case down the "these don't look related"
 * path. One pass over a list we already have is a cheap way to never re-learn
 * that.
 */
function matchedCount(diff: InventoryDiff): number {
  let matched = 0;
  for (const entry of diff.entries) {
    if (entry.status !== "only-a" && entry.status !== "only-b") matched += 1;
  }
  return matched;
}

/**
 * Whether these two repos look like the same model at all.
 *
 * Measured against the *smaller* side: a 300-tensor model compared against a
 * 30-tensor one that is genuinely its submodule should not be called unrelated
 * merely because 90% of A is missing from B.
 */
function looksUnrelated(diff: InventoryDiff): boolean {
  const smaller = Math.min(diff.a.tensors, diff.b.tensors);
  if (smaller === 0) return true;
  return matchedCount(diff) / smaller < UNRELATED_OVERLAP;
}

export function renderInventoryDiff(input: {
  diff: InventoryDiff;
  aLabel: string;
  bLabel: string;
  /** B's other stacks, when this diff is scoped to one of them. */
  droppedTowers?: readonly string[];
}): string {
  const { diff, aLabel, bLabel } = input;

  return [
    renderDiffHeadline(diff, aLabel, bLabel, input.droppedTowers ?? []),
    renderSideTotals(diff, aLabel, bLabel),
    looksUnrelated(diff)
      ? renderUnrelatedNotice(diff)
      : [
          renderDiffCounts(diff),
          renderDtypeShift(diff),
          renderEntryTable(diff, aLabel, bLabel),
        ].join(""),
  ].join("");
}

/**
 * The stacks that left the comparison when it was scoped to one tower.
 *
 * One line, deliberately — the reviewer's ruling (2026-08-13). Those tensors
 * answer *what did I drop*, which is a different question from *how does this
 * differ from the tower it came from*, and putting them in the diff population
 * buries the second under several hundred rows of the first. But they existed,
 * and a reader comparing a text-only build against a multimodal parent is owed
 * the fact that the parent also sees and hears.
 *
 * Rendered **inside the headline card, under the verdict**, because it is a
 * qualification of that sentence rather than a statement of its own: the verdict
 * says what B is, and this says which part of B it was measured against. As a
 * sibling between two cards it read as page furniture — bare text on the
 * background, belonging to neither.
 */
function renderDroppedTowers(towers: readonly string[]): string {
  if (towers.length === 0) return "";
  const list =
    towers.length === 1
      ? towers[0]
      : `${towers.slice(0, -1).join(", ")} and ${towers[towers.length - 1]}`;
  return `<p class="prose prose--muted diff-scope-note">Scoped to one stack. B also carries
    ${escapeHtml(String(list))} ${towers.length === 1 ? "tensors" : "towers"}, which A has none of
    and which are not counted below.</p>`;
}

/**
 * The verdict leads, as a sentence.
 *
 * `inventory-diff.ts` computes it precisely so the reader does not have to
 * assemble seven counters into a conclusion themselves; burying it under the
 * counters would waste the one line that answers "what am I looking at".
 */
function renderDiffHeadline(
  diff: InventoryDiff,
  aLabel: string,
  bLabel: string,
  droppedTowers: readonly string[] = [],
): string {
  // Not `.prose`: the verdict is this card's headline, not a paragraph somebody
  // settles in to read, and the 68ch prose measure broke a one-sentence answer
  // across two lines with a third of the card empty beside it. Same call as the
  // anatomy captions — a line that qualifies a card takes the card's width.
  return `<div class="card">
    ${cardTitle("Inventory diff")}
    <p class="diff-verdict"><strong>${escapeHtml(diff.verdict)}</strong></p>
    ${renderDroppedTowers(droppedTowers)}
    <div class="legend">
      <span class="key"><span class="swatch" style="background:${SERIES_A}"></span>A · ${escapeHtml(aLabel)}</span>
      <span class="key"><span class="swatch" style="background:${SERIES_B}"></span>B · ${escapeHtml(bLabel)}</span>
    </div>
  </div>`;
}

/**
 * The short-circuit.
 *
 * A six-hundred-row table of "only in A" is not a diff, it is two inventories
 * printed next to each other, and reading it costs a minute to reach the
 * conclusion this paragraph states in a sentence.
 */
function renderUnrelatedNotice(diff: InventoryDiff): string {
  const matched = matchedCount(diff);
  /*
   * Thin name overlap has **two** readings, and this notice used to state only
   * the harsher one.
   *
   * Two unrelated architectures share no tensor names. So do a model and the
   * checkpoint it was extracted from, when the two publishers spell their
   * stacks differently — and on that pair this paragraph said "nothing here
   * suggests one of these came from the other" directly beneath a grid that had
   * just matched every one of A's cells to a stack of B, and beneath a verdict
   * saying the naming differs. One page, three sentences, two of them agreeing
   * and the third contradicting both.
   *
   * `namingDiffers` is the detector that already tells them apart, for the
   * verdict; it travels on the diff now so this notice can ask the same
   * question rather than re-derive a weaker answer from the same arithmetic.
   */
  if (diff.namingDiffers) {
    return `<div class="notice notice--info">
      <strong>The names don't line up, but the shapes do.</strong>
      Not one tensor name appears on both sides, yet the two carry the same layers at the same
      widths — so these are the same architecture written by two publishers who spell it
      differently. The per-tensor table below is not drawn, because it pairs tensors by name and
      there is nothing here for it to pair. <strong>The map above is the comparison</strong>: it
      places tensors by what they do rather than by what they are called, so it lines the two up
      where this table cannot.
    </div>`;
  }
  return `<div class="notice notice--warn">
    <strong>These don't look related.</strong>
    ${matched === 0 ? "Not one tensor name appears on both sides" : `Only ${matched.toLocaleString()} tensor ${matched === 1 ? "name appears" : "names appear"} on both sides`},
    out of ${diff.a.tensors.toLocaleString()} in A and ${diff.b.tensors.toLocaleString()} in B.
    Compare mode is built for lineage — a base model against its own finetune or its own
    quantization — and nothing here suggests one of these came from the other.
    The per-tensor table is not drawn: with this little overlap every row would read
    “only in A” or “only in B”, which is the sentence above repeated
    ${(diff.a.tensors + diff.b.tensors).toLocaleString()} times.
    Different architectures also name their tensors differently, so an unrelated verdict
    is about names, not about capability.
  </div>`;
}

/** A against B on the three totals that describe a repo's size. */
function renderSideTotals(diff: InventoryDiff, aLabel: string, bLabel: string): string {
  const rows: ReadonlyArray<readonly [string, string, string, string, ExplainKey]> = [
    [
      "tensors",
      diff.a.tensors.toLocaleString(),
      diff.b.tensors.toLocaleString(),
      signedCount(diff.b.tensors - diff.a.tensors),
      "tensor-count",
    ],
    [
      "parameters",
      formatParams(diff.a.params),
      formatParams(diff.b.params),
      signedCount(diff.b.params - diff.a.params),
      "params",
    ],
    [
      "bytes",
      formatBytes(diff.a.bytes),
      formatBytes(diff.b.bytes),
      signedBytes(diff.b.bytes - diff.a.bytes),
      "bytes",
    ],
  ];
  return `<div class="card">
    ${cardTitle("Totals")}
    <table class="data-table">
      <thead><tr>
        <th scope="col"></th>
        <th scope="col">A · ${escapeHtml(aLabel)}</th>
        <th scope="col">B · ${escapeHtml(bLabel)}</th>
        <th scope="col">B − A</th>
      </tr></thead>
      <tbody>${rows
        .map(
          ([label, a, b, delta, key]) => `<tr>
            <th scope="row">${escapeHtml(label)}${explainIcon(key)}</th>
            <td class="mono">${escapeHtml(a)}</td>
            <td class="mono">${escapeHtml(b)}</td>
            <td class="mono">${escapeHtml(delta)}</td>
          </tr>`,
        )
        .join("")}</tbody>
    </table>
    <p class="chart-note">A parameter count that matches while the byte count drops is the
    signature of a quantization; both dropping together is a different model, not a
    re-encoding of this one.</p>
  </div>`;
}

function signedCount(delta: number): string {
  if (delta === 0) return "unchanged";
  return `${delta > 0 ? "+" : "−"}${formatParams(Math.abs(delta))}`;
}

function signedBytes(delta: number): string {
  if (delta === 0) return "unchanged";
  return `${delta > 0 ? "+" : "−"}${formatBytes(Math.abs(delta))}`;
}

function renderDiffCounts(diff: InventoryDiff): string {
  const cells: ReadonlyArray<StatCell> = [
    ["identical", diff.identical.toLocaleString(), "identical"],
    ["dtype changed", diff.dtypeChanged.toLocaleString(), "dtype-changed"],
    ["quantized", diff.quantized.toLocaleString(), "quantized"],
    ["dequantized", diff.dequantized.toLocaleString(), "dequantized"],
    ["shape changed", diff.shapeChanged.toLocaleString(), "shape-changed"],
    ["only in A", diff.onlyA.toLocaleString(), "only-a"],
    ["only in B", diff.onlyB.toLocaleString(), "only-b"],
    ["comparable", diff.comparable.toLocaleString(), "comparable"],
  ];
  return `<div class="card">
    ${cardTitle("What changed")}
    ${statGrid(cells)}
    <p class="chart-note"><strong>Comparable</strong> is the count that matters for the next
    step: a tensor is comparable when both sides reduce to the same logical shape, which is
    the only sense in which “the same tensor” has two versions. A shape change is a lineage
    mismatch and is reported rather than truncated to fit.</p>
  </div>`;
}

/**
 * Where the bytes moved, by dtype.
 *
 * A table rather than a chart on purpose: this is a handful of rows whose exact
 * byte counts are the thing being read, and the form heuristic sends "a few
 * exact numbers" to a table rather than to marks that have to be measured
 * against an axis to recover the number they encode.
 */
function renderDtypeShift(diff: InventoryDiff): string {
  if (diff.dtypeShift.length === 0) return "";
  return `<div class="card">
    ${cardTitle("Bytes by dtype")}
    <table class="data-table">
      <thead><tr>
        <th scope="col">dtype${explainIcon("dtype")}</th>
        <th scope="col">A</th><th scope="col">B</th>
        <th scope="col">B − A</th>
      </tr></thead>
      <tbody>${diff.dtypeShift
        .map(
          (row) => `<tr>
            <th scope="row" class="mono">${escapeHtml(row.dtype)}</th>
            <td class="mono">${escapeHtml(row.aBytes === 0 ? "—" : formatBytes(row.aBytes))}</td>
            <td class="mono">${escapeHtml(row.bBytes === 0 ? "—" : formatBytes(row.bBytes))}</td>
            <td class="mono">${escapeHtml(signedBytes(row.bBytes - row.aBytes))}</td>
          </tr>`,
        )
        .join("")}</tbody>
    </table>
    <p class="chart-note">A dtype present on one side only shows an em dash rather than
    “0 B”: absent and zero are different claims.</p>
  </div>`;
}

/**
 * The per-tensor table, minus the tensors that did not change.
 *
 * Identical tensors are the majority of any real diff and every one of their
 * rows says the same nothing. They stay counted in the summary above; what gets
 * a row here is what moved.
 */
function renderEntryTable(diff: InventoryDiff, aLabel: string, bLabel: string): string {
  const changed = diff.entries.filter((e) => e.status !== "identical");
  if (changed.length === 0) {
    return `<div class="card">
      ${cardTitle("Per tensor")}
      <p class="prose">Every one of the ${diff.identical.toLocaleString()} tensors matches on
      name, dtype and shape. Nothing in the inventory changed — which does not mean the
      <em>weights</em> are the same, only that their headers are. Open a tensor to compare
      the numbers.</p>
    </div>`;
  }

  const shown = changed.slice(0, MAX_ENTRY_ROWS);
  const truncated = changed.length - shown.length;

  return `<div class="card">
    ${cardTitle(
      `Per tensor<span class="card-title-note">${changed.length.toLocaleString()} changed ·
      ${diff.identical.toLocaleString()} identical not listed</span>`,
    )}
    <table class="data-table">
      <thead><tr>
        <th scope="col">tensor</th>
        <th scope="col">status${explainIcon("diff-status")}</th>
        <th scope="col">A · ${escapeHtml(aLabel)}</th>
        <th scope="col">B · ${escapeHtml(bLabel)}</th>
      </tr></thead>
      <tbody>${shown.map(renderEntryRow).join("")}</tbody>
    </table>
    ${
      truncated > 0
        ? `<p class="chart-note">Showing the first ${MAX_ENTRY_ROWS.toLocaleString()} changed
           tensors; ${truncated.toLocaleString()} more are counted above but not listed.</p>`
        : ""
    }
    <p class="chart-note">Status is spelled out in words, never carried by colour alone.</p>
  </div>`;
}

/**
 * A row's name is a button only when the row is comparable.
 *
 * Rows that are not — a shape change, a tensor present on one side only —
 * cannot produce a comparison, so offering a control that can only fail would
 * be an affordance that lies. The name is still shown, just not as something to
 * press, and the status column already says why.
 */
function entryName(entry: TensorDiffEntry): string {
  const name = escapeHtml(entry.name);
  const shown = entry.comparable
    ? `<button class="link-button mono" type="button" data-diff-tensor="${name}">${name}</button>`
    : `<span class="mono">${name}</span>`;
  /*
   * B's name, when the rekey paired two tensors that are not called the same
   * thing.
   *
   * The row header used to be one name because there was only ever one: pairing
   * was by name, so a row's existence meant both sides agreed. Now a row can
   * legitimately carry two, and showing A's alone would be the page picking a
   * publisher to believe — and would leave a reader unable to find the tensor in
   * the other repo, which is a thing they will want to do.
   *
   * `data-diff-tensor` stays A's name: it addresses A's inventory, which is what
   * the click goes on to read.
   */
  if (entry.bName === undefined) return shown;
  return `${shown}<span class="legend-note mono diff-b-name">B: ${escapeHtml(entry.bName)}</span>`;
}

function renderEntryRow(entry: TensorDiffEntry): string {
  return `<tr>
    <th scope="row">${entryName(entry)}</th>
    <td>${escapeHtml(STATUS_LABEL[entry.status])}${
      entry.note ? ` <span class="legend-note">${escapeHtml(entry.note)}</span>` : ""
    }</td>
    <td class="mono">${sideCell(entry.a)}</td>
    <td class="mono">${sideCell(entry.b)}</td>
  </tr>`;
}

function sideCell(tensor: InventoryTensor | undefined): string {
  if (!tensor) return "—";
  return escapeHtml(`${tensor.dtype} ${formatShape(tensor.shape)}`);
}

// --- One tensor, A against B ------------------------------------------------

export function renderTensorComparison(input: {
  tensorName: string;
  aLabel: string;
  bLabel: string;
  result: CompareResult;
  /**
   * Rows actually read and rows the tensor has, from A's read plan.
   *
   * Deliberately two numbers rather than a `sampled` boolean. The reviewer's
   * note was that the *fact* of sampling was visible but the *fraction* was
   * not, and a boolean cannot carry it — "sampled" over 2,048 of 4,864 rows and
   * "sampled" over 32 of 151,936 are very different claims about how much the
   * estimate can be trusted. Equal values mean a complete read.
   */
  sampling: { readonly rowsRead: number; readonly totalRows: number };
  logScale: boolean;
}): string {
  const { tensorName, aLabel, bLabel, result, sampling, logScale } = input;
  const sampled = sampling.rowsRead < sampling.totalRows;
  const c = result.comparison;

  return [
    `<h2 class="mono tensor-title">${escapeHtml(tensorName)}</h2>`,
    renderSides(aLabel, bLabel),
    renderNothingMeasured(c),
    renderSampledNotice(sampling, c),
    renderDroppedPairs(c),
    renderMovementCard(c, sampled),
    renderDirectionCard(c, sampled),
    renderScaleToggle(logScale),
    renderOverlayCard(result.histograms, aLabel, bLabel, logScale, c),
    renderDeltaCard(result.deltaHistogram, logScale, c),
    renderTableCard(tensorName, aLabel, bLabel, result, sampled),
  ].join("");
}

/** Which repo is A and which is B, with the colours they carry in both charts. */
function renderSides(aLabel: string, bLabel: string): string {
  return `<div class="card">
    ${cardTitle("Comparing")}
    <div class="legend">
      <span class="key"><span class="swatch" style="background:${SERIES_A}"></span>A · baseline · ${escapeHtml(aLabel)}</span>
      <span class="key"><span class="swatch" style="background:${SERIES_B}"></span>B · derived · ${escapeHtml(bLabel)}</span>
    </div>
    <p class="chart-note">A is the baseline throughout: it is the denominator of relF, so
    “how far did this move” is always measured as a fraction of where it started.</p>
  </div>`;
}

/** Every statistic is absent at once when no pair survived. Say that once. */
function renderNothingMeasured(c: TensorComparison): string {
  if (c.comparedCount > 0) return "";
  return `<div class="notice notice--warn">
    <strong>Nothing could be measured.</strong> All ${c.count.toLocaleString()} element pairs
    were dropped because at least one side of each was NaN or ±Infinity
    (${c.nonFiniteA.toLocaleString()} in A, ${c.nonFiniteB.toLocaleString()} in B). Every
    statistic below is an em dash rather than a zero — nothing was compared, which is a
    different claim from “nothing changed”.
  </div>`;
}

/**
 * The sampled caveat.
 *
 * Louder than `tensor-view.ts`'s equivalent needs to be, because a comparison
 * has a second failure mode a single-tensor analysis does not: if the two sides
 * were sampled at *different* rows, every statistic here would be comparing
 * unrelated weights while looking perfectly well-formed. The worker samples the
 * same row indices on both sides for exactly that reason, and saying so is what
 * lets a reader trust the number.
 */
function renderSampledNotice(
  sampling: { readonly rowsRead: number; readonly totalRows: number },
  c: TensorComparison,
): string {
  if (sampling.rowsRead >= sampling.totalRows) return "";
  const share = sampling.totalRows > 0 ? sampling.rowsRead / sampling.totalRows : 0;
  // Two significant figures, and never rounded up to a number that overstates
  // coverage: 32 rows of 151,936 is 0.021%, not "0%".
  const sharePercent =
    share >= 0.01 ? `${(share * 100).toFixed(1)}%` : `${(share * 100).toPrecision(2)}%`;
  return `<div class="notice notice--info">
    <strong>Sampled — these are not whole-tensor statistics.</strong>
    ${sampling.rowsRead.toLocaleString()} of ${sampling.totalRows.toLocaleString()} rows
    (<strong>${sharePercent}</strong> of the tensor), giving
    ${c.comparedCount.toLocaleString()} element pairs. The <em>same</em> row indices were read on both sides, so the
    pairs are genuinely matched and every statistic below is a like-for-like estimate — but
    it is an estimate. <strong>max |·| is a floor, not a maximum</strong> on either side and
    on the delta: the largest value in a sample can only understate the largest value in the
    tensor, and for a comparison whose subject is often a handful of edited weights that
    understatement is the one most worth knowing about.
  </div>`;
}

/**
 * Dropped pairs, reported rather than absorbed.
 *
 * `compare.ts` drops the whole *pair* when either side or their difference is
 * non-finite — stricter than the single-tensor path, so that A's σ and B's σ are
 * taken over the same index set and remain comparable to each other. The price
 * is that one bad element in B removes a good element of A from the statistics,
 * and a σ computed over 99.2% of a tensor has to say which 99.2%.
 */
function renderDroppedPairs(c: TensorComparison): string {
  if (c.nonFinitePairs === 0 || c.comparedCount === 0) return "";
  const coverage = c.count > 0 ? c.comparedCount / c.count : 0;
  return `<div class="notice notice--warn">
    <strong>${c.nonFinitePairs.toLocaleString()} element ${c.nonFinitePairs === 1 ? "pair was" : "pairs were"} dropped.</strong>
    Every statistic on this page is computed over ${percent(coverage, 2)} of the tensor
    (${c.comparedCount.toLocaleString()} of ${c.count.toLocaleString()} pairs). A pair is
    measured only when A, B and their difference are all finite —
    ${c.nonFiniteA.toLocaleString()} ${c.nonFiniteA === 1 ? "element" : "elements"} of A and
    ${c.nonFiniteB.toLocaleString()} of B were not. The whole pair goes, not just the
    offending side, so that A's σ and B's σ stay taken over the same elements and remain
    comparable to each other.
  </div>`;
}

/**
 * relF beside deltaParticipation — the pairing this whole view exists for.
 *
 * `compare.ts` is explicit that relF is a single norm-relative number and that
 * a surgical change and a diffuse one with the same error norm are
 * indistinguishable to it. deltaParticipation is the companion that separates
 * them, and it is the reading a user can act on: "0.02 of the tensor moved" is
 * actionable where "relF 0.03" is not. They share one card so that nobody can
 * see one without the other.
 */
function renderMovementCard(c: TensorComparison, sampled: boolean): string {
  const relF = maybeStat(
    "relF",
    c.relF,
    "‖A‖ is zero over the measured pairs, so there is nothing for the change to be relative to. The absolute size of the change is still reported as the delta's σ below.",
  );
  const participation = maybeStat(
    "delta participation",
    c.deltaParticipation,
    c.comparedCount === 0
      ? "Nothing was measured, so there is no change whose concentration could be located."
      : "The delta is identically zero — B reproduces A exactly over every measured pair. There is no concentration of a change that did not happen.",
  );

  const cells: ReadonlyArray<StatCell> = [
    ["relF · how far B moved", relF.text, "relf"],
    ["participation · how much moved it", participation.text, "participation"],
    ["changed weights", percent(c.changedFraction, 2), "changed-fraction"],
    [
      sampled ? "largest single edit (floor)" : "largest single edit",
      sig(c.deltaMoments.maxAbs),
      "largest-edit",
    ],
  ];

  return `<div class="card">
    ${cardTitle("How far B moved, and how much of the tensor did the moving")}
    ${statGrid(cells)}
    <p class="prose">${participationGloss(c)}</p>
    ${reasonNotes([relF, participation])}
    <p class="chart-note">These two are never shown apart. relF is a single norm-relative
    number, so a change to a handful of weights and a change to all of them score the same
    whenever their error norms match; participation is what tells those apart, and it is
    scale-invariant, so it is genuinely independent of relF rather than a restatement of it.</p>
  </div>`;
}

/**
 * The plain-language reading of `deltaParticipation`.
 *
 * The most useful sentence on the screen, and the reason the statistic is
 * reported instead of the kurtosis it is the reciprocal of: a fraction of the
 * tensor is a quantity a reader can picture, and "kurtosis 50" is not.
 */
function participationGloss(c: TensorComparison): string {
  const p = c.deltaParticipation;
  if (p === undefined || !Number.isFinite(p)) {
    return c.comparedCount === 0
      ? `<strong>No change could be located.</strong> Nothing was measured, so there is no
         distribution of edits to describe.`
      : `<strong>Nothing moved.</strong> B reproduces A exactly over every measured pair, so
         there is no change to locate — not a diffuse one and not a concentrated one.`;
  }

  if (p >= PARTICIPATION_UNIFORM) {
    return `<strong>Uniform drift — the change is the whole tensor.</strong>
      Participation is ${sig(p, 3)}, near its maximum of 1.0, which means essentially every
      weight moved by a similar magnitude. This is what a rescale, a dtype round trip, or an
      added offset looks like: nothing was singled out.`;
  }

  if (p < PARTICIPATION_CONCENTRATED) {
    const elements = Math.max(1, Math.round(p * c.comparedCount));
    return `<strong>Concentrated — the change lives in about ${percent(p, 2)} of the
      weights.</strong> Participation is ${sig(p, 3)}, so the movement carries the mass of
      roughly ${elements.toLocaleString()} of ${c.comparedCount.toLocaleString()} measured
      weights rather than being spread across them. A LoRA merge into a low-rank subspace, a
      repair of a few outlier weights, and a quantization that only mangled the tail all land
      here — and so does a corruption, which is why this is worth looking at rather than
      scoring.`;
  }

  const gaussian = p >= 0.28 && p <= 0.4;
  return `<strong>Diffuse — everything moved a little, some more than others.</strong>
    Participation is ${sig(p, 3)}${
      gaussian
        ? ", which is essentially the Gaussian value of ⅓ — the ordinary finetune signature."
        : ", between the ⅓ of a Gaussian delta and the 1.0 of a uniform one."
    } The change is spread across the tensor rather than sitting in a few weights.`;
}

/**
 * cosine beside the two σ — the second mandatory pairing.
 *
 * A pure rescale leaves cosine at 1.0 with an arbitrarily large relF, and that
 * combination is a finding rather than noise: B did not learn anything, it was
 * multiplied. Cosine alone cannot say so (it is scale-invariant, which is the
 * point) and relF alone cannot either, so the two σ have to be on the same card
 * for the reading to be available at all.
 */
function renderDirectionCard(c: TensorComparison, sampled: boolean): string {
  const cosine = maybeStat(
    "cosine",
    c.cosine,
    "One side has zero norm over the measured pairs, or nothing was measured. A zero vector has no direction, so there is no angle to report — and 0 ÷ 0 is not “orthogonal”.",
    6,
  );

  const cells: ReadonlyArray<StatCell> = [
    ["cosine · direction", cosine.text, "cosine"],
    ["σ (A)", sig(c.a.sigma), "sigma"],
    ["σ (B)", sig(c.b.sigma), "sigma"],
    [
      "σ (B) ÷ σ (A)",
      c.a.sigma > 0 ? sig(c.b.sigma / c.a.sigma, 4) : "—",
      "sigma-ratio",
    ],
    [sampled ? "max |A| (floor)" : "max |A|", sig(c.a.maxAbs), "max-abs"],
    [sampled ? "max |B| (floor)" : "max |B|", sig(c.b.maxAbs), "max-abs"],
  ];

  return `<div class="card">
    ${cardTitle("Which way B points, and how big it is")}
    ${statGrid(cells)}
    ${renderRescaleFinding(c)}
    ${reasonNotes([cosine])}
    <p class="chart-note">Cosine is scale-invariant, so it answers “did B rotate away from
    A” and deliberately cannot see a change of size. The two σ are what see that, which is
    why they sit beside it rather than in a table of their own.</p>
  </div>`;
}

/** The rescale reading, stated when the numbers actually support it. */
function renderRescaleFinding(c: TensorComparison): string {
  const { cosine, relF } = c;
  if (cosine === undefined || relF === undefined) return "";
  if (cosine < 0.9999 || relF < 0.005 || c.a.sigma <= 0) return "";
  const ratio = c.b.sigma / c.a.sigma;
  if (!Number.isFinite(ratio)) return "";
  return `<div class="notice notice--info">
    <strong>This looks like a pure rescale.</strong> Cosine is ${sig(cosine, 6)} — B points in
    essentially the same direction as A — while relF is ${sig(relF, 3)}, so B is a long way
    from A in absolute terms, and σ(B) is ${sig(ratio, 4)}× σ(A). A tensor that moved without
    rotating was multiplied, not retrained. That combination is a finding: a finetune that
    only rescaled has learned nothing, and a quantization round trip that rescaled has a
    scale-convention bug.
  </div>`;
}

/**
 * One scale control for both charts.
 *
 * Deliberately above the two chart cards rather than inside either: a filter
 * that scopes more than one chart belongs to all of them, and a per-card toggle
 * would let the two charts end up on different axes while sitting next to each
 * other. The button is a real button with `aria-pressed`, so it is reachable and
 * its state is announced; `main.ts` binds it by id.
 */
function renderScaleToggle(logScale: boolean): string {
  return `<div class="chips" role="group" aria-label="Scale of the count axis">
    <button class="chip" type="button" id="compare-log-toggle" aria-pressed="${logScale}">
      log count axis
    </button>
  </div>`;
}

// --- Charts -----------------------------------------------------------------

/**
 * A stepped outline over the bins, closed to the baseline so the same path can
 * carry both the wash and the stroke.
 */
function stepPath(
  counts: readonly number[],
  scaleY: (count: number) => number,
  x0: number,
  binWidth: number,
  baseline: number,
): string {
  const parts: string[] = [`M ${x0.toFixed(2)} ${baseline.toFixed(2)}`];
  counts.forEach((count, index) => {
    const y = scaleY(count).toFixed(2);
    parts.push(`L ${(x0 + index * binWidth).toFixed(2)} ${y}`);
    parts.push(`L ${(x0 + (index + 1) * binWidth).toFixed(2)} ${y}`);
  });
  parts.push(`L ${(x0 + counts.length * binWidth).toFixed(2)} ${baseline.toFixed(2)}`);
  parts.push("Z");
  return parts.join(" ");
}

function countScale(
  maxCount: number,
  logScale: boolean,
  baseline: number,
  plotTop: number,
): (count: number) => number {
  const top = maxCount || 1;
  return (count: number) => {
    const fraction = logScale
      ? Math.log10(1 + count) / Math.log10(1 + top)
      : count / top;
    return baseline - fraction * (baseline - plotTop);
  };
}

/**
 * A and B on one shared range.
 *
 * The shared range is the whole point and the caption says so: two
 * distributions each scaled to its own ±4.5σ would draw a rescale as no change
 * at all, which is precisely the difference compare mode exists to show. The
 * count axis is shared for the same reason — two independently scaled y axes
 * would make the smaller side's peak look equal to the larger one's.
 */
function renderOverlayCard(
  histograms: SharedHistograms | undefined,
  aLabel: string,
  bLabel: string,
  logScale: boolean,
  c: TensorComparison,
): string {
  if (!histograms) {
    return `<div class="card">
      ${cardTitle("Weight distributions, A and B")}
      <p class="prose prose--muted">No distributions to draw — both sides are degenerate
      (σ = 0), so there is no range to bin. A constant tensor has no shape to show.</p>
    </div>`;
  }

  const a = Array.from(histograms.a.counts);
  const b = Array.from(histograms.b.counts);
  const bins = Math.max(a.length, b.length);
  const maxCount = Math.max(histograms.a.maxCount, histograms.b.maxCount);
  const baseline = OVERLAY_HEIGHT - 30;
  const plotTop = 20;
  const scaleY = countScale(maxCount, logScale, baseline, plotTop);
  const binWidth = (CHART_WIDTH - 2 * CHART_PAD) / bins;

  const series = (counts: number[], color: string) =>
    `<path d="${stepPath(counts, scaleY, CHART_PAD, binWidth, baseline)}"
       fill="${color}" fill-opacity="0.1" stroke="${color}" stroke-width="2"
       stroke-linejoin="round"/>`;

  const label = `histogram of weight values for A and B, both binned on the same range
    ${sig(histograms.a.lo, 3)} to ${sig(histograms.a.hi, 3)}, ${bins} bins,
    ${logScale ? "logarithmic" : "linear"} count axis`;

  return `<div class="card">
    ${cardTitle(
      `Weight distributions, A and B<span class="card-title-note">one shared range</span>`,
    )}
    <svg viewBox="0 0 ${CHART_WIDTH} ${OVERLAY_HEIGHT}" role="img"
         aria-label="${escapeHtml(label.replace(/\s+/g, " "))}">
      <line x1="${CHART_PAD}" x2="${CHART_WIDTH - CHART_PAD}" y1="${baseline}" y2="${baseline}"
            stroke="var(--axis)"/>
      ${series(a, SERIES_A)}
      ${series(b, SERIES_B)}
      <text x="${CHART_PAD}" y="${plotTop - 6}" font-size="10" fill="var(--muted)">
        ${escapeHtml(maxCount.toLocaleString())} in the tallest bin${logScale ? " · log axis" : ""}
      </text>
      <text x="${CHART_PAD}" y="${OVERLAY_HEIGHT - 10}" font-size="10" fill="var(--muted)">${sig(histograms.a.lo, 3)}</text>
      <text x="${CHART_WIDTH / 2}" y="${OVERLAY_HEIGHT - 10}" font-size="10" fill="var(--muted)" text-anchor="middle">0</text>
      <text x="${CHART_WIDTH - CHART_PAD}" y="${OVERLAY_HEIGHT - 10}" font-size="10" fill="var(--muted)" text-anchor="end">${sig(histograms.a.hi, 3)}</text>
    </svg>
    <div class="legend">
      <span class="key"><span class="swatch" style="background:${SERIES_A}"></span>A · ${escapeHtml(aLabel)}</span>
      <span class="key"><span class="swatch" style="background:${SERIES_B}"></span>B · ${escapeHtml(bLabel)}</span>
      <span class="legend-note">shared range ±${sig(histograms.halfWidth, 3)} · shared count axis</span>
    </div>
    <p class="chart-note"><strong>Both sides are binned on one range</strong>, taken from the
    wider of the two (±${sig(histograms.halfWidth, 3)}). That costs the narrower side some
    resolution and is the only honest way to draw this: two distributions each scaled to its
    own ±4.5σ would render a pure rescale as no change at all.</p>
    ${outsideNote("A", histograms.a, c.comparedCount)}
    ${outsideNote("B", histograms.b, c.comparedCount)}
    ${renderBinTable(histograms, aLabel, bLabel)}
  </div>`;
}

/**
 * The delta's own distribution, on its own range.
 *
 * Not a third series on the chart above, and the reason is arithmetic rather
 * than aesthetic: a finetune delta is routinely two or three orders of magnitude
 * narrower than the weights themselves, so on the shared range it would be a
 * single invisible spike in the middle bin — a mark that says "no change"
 * about the one quantity the page is asking about.
 */
function renderDeltaCard(
  deltaHistogram: Histogram | undefined,
  logScale: boolean,
  c: TensorComparison,
): string {
  if (!deltaHistogram) {
    return `<div class="card">
      ${cardTitle("The change itself, B − A")}
      <p class="prose prose--muted">No delta distribution to draw — the difference is
      identically zero, or nothing was measured. There is no spread in a change that did
      not happen.</p>
    </div>`;
  }

  const counts = Array.from(deltaHistogram.counts);
  const baseline = DELTA_HEIGHT - 30;
  const plotTop = 20;
  const scaleY = countScale(deltaHistogram.maxCount, logScale, baseline, plotTop);
  const binWidth = (CHART_WIDTH - 2 * CHART_PAD) / counts.length;

  const label = `histogram of the per-element change B minus A, from
    ${sig(deltaHistogram.lo, 3)} to ${sig(deltaHistogram.hi, 3)},
    ${logScale ? "logarithmic" : "linear"} count axis`;

  return `<div class="card">
    ${cardTitle(
      `The change itself, B − A<span class="card-title-note">its own range</span>`,
    )}
    <svg viewBox="0 0 ${CHART_WIDTH} ${DELTA_HEIGHT}" role="img"
         aria-label="${escapeHtml(label.replace(/\s+/g, " "))}">
      <line x1="${CHART_PAD}" x2="${CHART_WIDTH - CHART_PAD}" y1="${baseline}" y2="${baseline}"
            stroke="var(--axis)"/>
      <path d="${stepPath(counts, scaleY, CHART_PAD, binWidth, baseline)}"
            fill="${SERIES_DELTA}" fill-opacity="0.1" stroke="${SERIES_DELTA}"
            stroke-width="2" stroke-linejoin="round"/>
      <text x="${CHART_PAD}" y="${plotTop - 6}" font-size="10" fill="var(--muted)">
        ${escapeHtml(deltaHistogram.maxCount.toLocaleString())} in the tallest bin${logScale ? " · log axis" : ""}
      </text>
      <text x="${CHART_PAD}" y="${DELTA_HEIGHT - 10}" font-size="10" fill="var(--muted)">${sig(deltaHistogram.lo, 3)}</text>
      <text x="${CHART_WIDTH / 2}" y="${DELTA_HEIGHT - 10}" font-size="10" fill="var(--muted)" text-anchor="middle">0</text>
      <text x="${CHART_WIDTH - CHART_PAD}" y="${DELTA_HEIGHT - 10}" font-size="10" fill="var(--muted)" text-anchor="end">${sig(deltaHistogram.hi, 3)}</text>
    </svg>
    ${statGrid([
      ["σ of the change", sig(c.deltaMoments.sigma), "delta-sigma"],
      ["mean change", sig(c.deltaMoments.mean), "delta-mean"],
      ["mean |change|", sig(c.deltaMoments.meanAbs), "delta-mean-abs"],
      ["unchanged weights", percent(c.unchangedFraction, 2), "unchanged-fraction"],
    ])}
    <p class="chart-note">Drawn on its own range rather than as a third series above: a
    finetune's delta is routinely orders of magnitude narrower than the weights, so on the
    shared range it would be one invisible spike. A non-zero <strong>mean change</strong> is
    a systematic bias in the edit — an added offset or a rescale — which an about-zero σ
    cannot see.</p>
    ${outsideNote("the delta", deltaHistogram, c.comparedCount)}
  </div>`;
}

/**
 * The tail mass the window cannot show, stated rather than dropped.
 *
 * Same rule `tensor-view.ts` follows and for the same reason: the values
 * outside ±4.5σ are the outliers that decide whether a scale works, and a chart
 * that silently discards them draws a tidy bell curve for a tensor that is
 * anything but.
 */
function outsideNote(which: string, h: Histogram, comparedCount: number): string {
  if (h.outsideRange === 0) {
    return `<p class="chart-note">Every value of ${escapeHtml(which)} falls inside the drawn range.</p>`;
  }
  const share = comparedCount > 0 ? percent(h.outsideRange / comparedCount, 3) : "—";
  return `<p class="chart-note"><strong>${h.outsideRange.toLocaleString()}</strong> values of
    ${escapeHtml(which)} (${share}) fall outside the drawn range and are not
    plotted — ${h.belowRange.toLocaleString()} below, ${h.aboveRange.toLocaleString()} above.
    That tail is the interesting part.</p>`;
}

/**
 * The overlaid chart's text alternative.
 *
 * The chart carries two series in colour; this is the same data as numbers, so
 * the reading never depends on telling two hues apart. Empty bins are omitted —
 * they are the majority of a 121-bin window and listing them would bury the
 * bins that have something in them.
 */
function renderBinTable(
  histograms: SharedHistograms,
  aLabel: string,
  bLabel: string,
): string {
  const a = Array.from(histograms.a.counts);
  const b = Array.from(histograms.b.counts);
  const bins = Math.max(a.length, b.length);
  const lo = histograms.a.lo;
  const width = histograms.a.binWidth;

  const rows: string[] = [];
  for (let i = 0; i < bins; i += 1) {
    const ca = a[i] ?? 0;
    const cb = b[i] ?? 0;
    if (ca === 0 && cb === 0) continue;
    const from = lo + i * width;
    rows.push(`<tr>
      <th scope="row" class="mono">${escapeHtml(sig(from, 3))}</th>
      <td class="mono">${escapeHtml(ca.toLocaleString())}</td>
      <td class="mono">${escapeHtml(cb.toLocaleString())}</td>
    </tr>`);
  }

  return `<details>
    <summary class="chart-note">The two distributions as a table (${rows.length} non-empty bins)</summary>
    <table class="data-table">
      <thead><tr>
        <th scope="col">bin from</th>
        <th scope="col">A · ${escapeHtml(aLabel)}</th>
        <th scope="col">B · ${escapeHtml(bLabel)}</th>
      </tr></thead>
      <tbody>${rows.join("")}</tbody>
    </table>
    <p class="chart-note">Bins empty on both sides are omitted. Bin width
    ${escapeHtml(sig(width, 3))}.</p>
  </details>`;
}

/**
 * Every number on this page, in one copyable table.
 *
 * Discharges the light-mode contrast relief the palette owes for `--series-3`,
 * and doubles as the "let me paste these into my notes" affordance a research
 * audience reaches for anyway. Absent statistics stay em dashes here too — the
 * table is a second rendering of the same facts, not a place where "undefined"
 * is allowed to leak out as a word.
 */
function renderTableCard(
  tensorName: string,
  aLabel: string,
  bLabel: string,
  result: CompareResult,
  sampled: boolean,
): string {
  const c = result.comparison;
  const rows: Array<readonly [string, string]> = [
    ["tensor", tensorName],
    ["A (baseline)", aLabel],
    ["B (derived)", bLabel],
    ["read mode", sampled ? "sampled rows, matched on both sides" : "complete"],
    ["elements per side", c.count.toLocaleString()],
    ["pairs measured", c.comparedCount.toLocaleString()],
    ["pairs dropped (non-finite)", c.nonFinitePairs.toLocaleString()],
    ["non-finite in A", c.nonFiniteA.toLocaleString()],
    ["non-finite in B", c.nonFiniteB.toLocaleString()],
    ["cosine", sig(c.cosine, 6)],
    ["relF (‖B−A‖ ÷ ‖A‖)", sig(c.relF)],
    ["delta participation", sig(c.deltaParticipation)],
    ["changed fraction", sig(c.changedFraction)],
    ["unchanged fraction", sig(c.unchangedFraction)],
    ["changed count", c.changedCount.toLocaleString()],
    ["sigma A", sig(c.a.sigma)],
    ["sigma B", sig(c.b.sigma)],
    [sampled ? "max |A| (lower bound)" : "max |A|", sig(c.a.maxAbs)],
    [sampled ? "max |B| (lower bound)" : "max |B|", sig(c.b.maxAbs)],
    ["delta sigma", sig(c.deltaMoments.sigma)],
    ["delta mean", sig(c.deltaMoments.mean)],
    ["delta mean |·|", sig(c.deltaMoments.meanAbs)],
    [
      sampled ? "delta max |·| (lower bound)" : "delta max |·|",
      sig(c.deltaMoments.maxAbs),
    ],
    ["delta kurtosis", sig(c.deltaMoments.kurtosis, 3)],
    ["elements analyzed", result.analyzed.toLocaleString()],
    ["worker time (ms)", result.elapsedMs.toFixed(1)],
  ];

  return `<details class="card">
    ${cardSummaryTitle("All values, as a table")}
    <table class="data-table data-table--kv">
      <tbody>${rows
        .map(
          ([key, value]) =>
            `<tr><th scope="row">${escapeHtml(key)}</th><td class="mono">${escapeHtml(value)}</td></tr>`,
        )
        .join("")}</tbody>
    </table>
    <p class="chart-note">An em dash means the statistic is not defined for these two
    tensors, not that it is zero. The cards above say which one and why.</p>
  </details>`;
}
